import useConnectWallet from "@/store/wallet/useConnectWallet";
import { useCallback, useEffect, useState } from "react";

// 直推用户信息类型 - 合约返回的原始结构
export interface DirectLinkUserType {
    userL0Addr: string[];   // 用户地址数组
    amount: bigint[];       // 质押金额数组
    poolBalance: bigint[];  // 当前算力矿池余额数组
}

// 每次获取的数量限制
const limit = 100;

/**
 * useGetDirectLinkUsers - 分页获取用户直推用户列表
 */
const useGetDirectLinkUsers = () => {
    const { address, contractEvm: { QueryFunction } } = useConnectWallet();

    // 状态管理 - 存储合并后的数据
    const [data, setData] = useState<DirectLinkUserType>({
        userL0Addr: [],
        amount: [],
        poolBalance: []
    });
    const [start, setStart] = useState<string>("0");
    const [end, setEnd] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);

    /**
     * 初始化状态 - 当地址或合约变化时重置数据
     */
    useEffect(() => {
        setData({
            userL0Addr: [],
            amount: [],
            poolBalance: []
        });
        setStart("0");
        setEnd(false);
        setLoading(false);
        setReady(true);
    }, [address, QueryFunction]);

    /**
     * getDirectLinkUsers - 分页获取直推用户列表
     * 获取用户直推用户地址，以及他们的质押金额，当前算力矿池余额
     */
    const getDirectLinkUsers = useCallback(async () => {
        if (!QueryFunction || !address || end || loading) return;

        try {
            setLoading(true);

            // 调用合约方法获取直推用户列表
            const list = await QueryFunction.getDirLinkUsers(address, start, limit);

            // 如果返回数据少于 limit，说明已经到底
            if (list.userL0Addr.length < limit) {
                setEnd(true);
            } else {
                // 更新下一页的起始位置
                setStart((parseInt(start) + limit).toString());
            }

            // 合并数组数据
            setData((prev) => ({
                userL0Addr: [...prev.userL0Addr, ...list.userL0Addr],
                amount: [...prev.amount, ...list.amount],
                poolBalance: [...prev.poolBalance, ...list.poolBalance]
            }));

        } catch (error) {
            console.log('useGetDirectLinkUsers error:', error);
        } finally {
            setLoading(false);
        }
    }, [QueryFunction, address, start, end, loading]);

    /**
     * 当 ready 状态变为 true 时，执行第一次数据获取
     */
    useEffect(() => {
        if (ready) {
            getDirectLinkUsers();
        }
    }, [ready, getDirectLinkUsers]);

    return {
        getDirectLinkUsers,
        data,
        loading,
        end
    };
};

export default useGetDirectLinkUsers;
